import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  output,
  Signal,
} from '@angular/core';

import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Slider } from 'primeng/slider';
import {
  PublicSubscriptionForm,
  PublicSubscriptionFormGroup,
} from '@web/shared/module/shop';
import { Nillable, valueOrDefault } from '@web/utils';
import { Button } from 'primeng/button';
import {
  FormControlComponent,
  PrimengButtonDirective,
  WarningComponent,
} from '@web/shared/ui';

import { toSignal } from '@angular/core/rxjs-interop';
import { startWith } from 'rxjs';

@Component({
  selector: 'app-public-subscription-form',
  imports: [
    ReactiveFormsModule,
    Slider,
    Button,
    PrimengButtonDirective,
    FormControlComponent,
    WarningComponent,
  ],
  templateUrl: './public-subscription-form.component.html',
  styleUrl: './public-subscription-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PublicSubscriptionFormComponent implements OnInit {
  public readonly data = input<Nillable<PublicSubscriptionForm>>();
  public readonly submitClickEvent = output<PublicSubscriptionForm>();
  public readonly cancelClickEvent = output<void>();

  protected readonly minRangeValue = 5;
  protected readonly maxRangeValue = 15;
  protected readonly initialValue = 5;
  protected readonly form: PublicSubscriptionFormGroup;
  protected readonly currentCardChargeDay: Signal<number>;
  protected readonly shouldShowStandardDayMessage: Signal<boolean>;
  protected readonly shouldShowConcreteDayMessage: Signal<boolean>;
  protected readonly shouldShowNextMonthMessage: Signal<boolean>;

  private readonly builder = inject(FormBuilder);

  /* prettier-ignore */
  public constructor() {
    this.form = this.buildForm();
    this.currentCardChargeDay = this.prepareCurrentCardChargeDaySignal(this.form);
    this.shouldShowStandardDayMessage = this.prepareShouldShowStandardDayMessage();
    this.shouldShowConcreteDayMessage = this.prepareShouldShowConcreteDayMessage();
    this.shouldShowNextMonthMessage = this.prepareShouldShowNextMonthMessage();
  }

  public ngOnInit(): void {
    this.tryToFillForm();
  }

  public handleCancelButtonClick(): void {
    this.cancelClickEvent.emit();
  }

  public handleSubmitButtonClick(): void {
    this.submitClickEvent.emit(this.toData());
  }

  /* prettier-ignore */
  private buildForm(): PublicSubscriptionFormGroup {
    return this.builder.group({
      cardChargeDay: this.builder.nonNullable.control<number>(this.initialValue, [Validators.required]),
    });
  }

  private prepareCurrentCardChargeDaySignal(
    form: PublicSubscriptionFormGroup,
  ): Signal<number> {
    const control = form.controls.cardChargeDay;

    return toSignal(control.valueChanges.pipe(startWith(control.value)), {
      initialValue: control.value,
    });
  }

  private tryToFillForm(): void {
    const data = this.data();

    this.form.patchValue({
      cardChargeDay: valueOrDefault(data?.cardChargeDay, this.initialValue),
    });
  }

  private toData(): PublicSubscriptionForm {
    const data = this.form.getRawValue();

    return new PublicSubscriptionForm({
      cardChargeDay: data.cardChargeDay,
    });
  }

  private prepareShouldShowStandardDayMessage(): Signal<boolean> {
    return computed(() => this.currentCardChargeDay() === 5);
  }

  private prepareShouldShowConcreteDayMessage(): Signal<boolean> {
    return computed(() => this.currentCardChargeDay() > 5);
  }

  private prepareShouldShowNextMonthMessage(): Signal<boolean> {
    return computed(() => this.currentCardChargeDay() < new Date().getDate());
  }
}
